import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import Slider from 'rc-slider';
import type {
  AlignItemsProps,
  ColorProps,
  DisplayProps,
  FlexDirectionProps,
  GridGapProps,
  JustifyContentProps,
  SizeProps,
  SpaceProps,
  WidthProps,
} from 'styled-system';
import {
  alignItems,
  color,
  display,
  flexDirection,
  gridGap,
  justifyContent,
  size,
  space,
  width,
} from 'styled-system';

import { useMatchesViewport } from '../../hooks/media';
import { useLocalization } from '../../providers/LocaleProvider';
import type { FillProps } from '../../theme/system-types';
import type { BoxSizingProps } from '../../theme/system-utilities';
import { boxSizing, fill } from '../../theme/system-utilities';
import { Button } from '../Button';
import Icon from '../Icon';
import { BodyRegularBR2 } from '../Typography';
import { SliderContainer } from './Container';
import type { UseRangeSlidersParams } from './hooks';
import { useRangeSlider } from './hooks';
import ValueBox from './ValueBox';

const ARROW_ICON_SIZE = 24;
export const RANGE_SLIDER_TEST_ID = 'range-slider';

export interface RangeSliderProps extends UseRangeSlidersParams {
  name: string;
  unit: string;
}

const RangeSliderContainer = styled(SliderContainer)<
  SpaceProps & DisplayProps & FlexDirectionProps & WidthProps & BoxSizingProps
>`
  ${space};
  ${display};
  ${flexDirection};
  ${width};
  ${boxSizing};
`;

const rangeSliderContainerStyles: ComponentProps<typeof RangeSliderContainer> = {
  boxSizing: 'border-box',
  px: 'sp_0',
  display: 'flex',
  flexDirection: 'column',
  width: 1,
};

const RangeSliderValues = styled.div<
  DisplayProps & AlignItemsProps & JustifyContentProps & SpaceProps & ColorProps & GridGapProps
>`
  ${display};
  ${alignItems};
  ${justifyContent};
  ${space};
  ${color};
  ${gridGap};
`;

const rangeSliderValuesStyles: ComponentProps<typeof RangeSliderValues> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  color: 'sys.neutral.text.default',
  mb: 'sp_12',
  gridGap: 'sp_16',
};

const valuesSeparatorStyles: Partial<ComponentProps<typeof BodyRegularBR2>> = {
  color: 'sys.neutral.text.default',
  fontWeight: 'normal',
};

const ArrowIcon = styled(Icon)<FillProps & SizeProps>`
  ${size};
  ${fill};
`;

const ApplyButton = styled(Button)<SpaceProps>`
  ${space};
`;

const arrowIconStyles: Partial<ComponentProps<typeof ArrowIcon>> = {
  size: ARROW_ICON_SIZE,
  fill: 'sys.neutral.background.default',
};

const applyButtonStyles: ComponentProps<typeof ApplyButton> = {
  ml: ['sp_16', null, 'sp_4', 'sp_16'],
};

const RangeSlider = ({
  name,
  unit,
  withApplyButton,
  onRangeChange = () => undefined,
  trackRangeChange = () => undefined,
  ...initialRange
}: RangeSliderProps) => {
  const { t } = useLocalization();

  const {
    currentValues,
    min,
    max,
    handleApply,
    onMinInputChange,
    onMaxInputChange,
    isMinHandleDragging,
    isMaxHandleDragging,
    handleRangeOnChange,
    handleRangeOnAfterChange,
  } = useRangeSlider({
    withApplyButton,
    onRangeChange,
    trackRangeChange,
    ...initialRange,
  });

  const {
    matches: { matchesLargeDesktop },
  } = useMatchesViewport();
  const [currentMin, currentMax] = currentValues;

  const isApplyButtonDisabled =
    initialRange.selectedMax === currentMax && initialRange.selectedMin === currentMin;

  return (
    <RangeSliderContainer {...rangeSliderContainerStyles} data-testid={RANGE_SLIDER_TEST_ID}>
      <RangeSliderValues {...rangeSliderValuesStyles}>
        <ValueBox
          ariaLabel={`${String(currentMin)} ${unit} ${name} ${t('h24_min_value')}`}
          isHighlighted={isMinHandleDragging}
          currentValue={String(currentMin)}
          unit={unit}
          onChange={onMinInputChange}
        />
        <BodyRegularBR2 {...valuesSeparatorStyles} as="span">
          {matchesLargeDesktop ? t('h24_to') : t('h24_short_to')}
        </BodyRegularBR2>
        <ValueBox
          ariaLabel={`${String(currentMax)} ${unit} ${name} ${t('h24_max_value')}`}
          isHighlighted={isMaxHandleDragging}
          currentValue={String(currentMax)}
          unit={unit}
          onChange={onMaxInputChange}
        />
        {withApplyButton && (
          <ApplyButton
            {...applyButtonStyles}
            onClick={e => handleApply(e)}
            disabled={isApplyButtonDisabled}
            aria-label={t('h24_apply')}
          >
            <ArrowIcon {...arrowIconStyles} name="arrowStrongRight" />
          </ApplyButton>
        )}
      </RangeSliderValues>
      <Slider
        range
        allowCross={false}
        value={currentValues}
        min={min}
        max={max}
        onChange={handleRangeOnChange}
        onChangeComplete={handleRangeOnAfterChange}
        ariaLabelForHandle={[`${name}-min`, `${name}-max`]}
      />
    </RangeSliderContainer>
  );
};

export default RangeSlider;
